// DOM for the feature views: lanes (RM, OSMLR), LM lane restrictions, CaJ. Glyphs and
// line styles are labelled conventions; values are shown as tagged and on hover.
import { ACCESS_CLASS_COLORS } from "../layers/style/util/restrictionClass.mjs";
import { splitUnescaped } from "./lrValues.mjs";
import {
  el,
  renderPlainValue,
  renderProfile,
  renderViewRaw,
} from "./renderValues.mjs";

const DIR_GLYPH = {
  forward: "↑",
  backward: "↓",
  both_ways: "↕",
  reversible: "⇅",
};
const TURN_GLYPH = {
  through: "↑",
  left: "↰",
  right: "↱",
  slight_left: "↖",
  slight_right: "↗",
  sharp_left: "↙",
  sharp_right: "↘",
  reverse: "↶",
  merge_to_left: "⇖",
  merge_to_right: "⇗",
  merge: "⇑",
  none: "·",
};
const DIVIDERS = ["none", "solid", "double_solid", "long_dash", "short_dash"];
const DIVIDER_WORD = {
  none: "gap = none",
  solid: "solid",
  double_solid: "double = double_solid",
  long_dash: "dashed = long_dash",
  short_dash: "dotted = short_dash",
};
const CLASS_LABEL = {
  busOnly: "bus only",
  taxiAndBus: "taxi and bus",
  taxiOnly: "taxi only",
  noTrucks: "no trucks",
  noBusNoTruck: "no bus, no trucks",
  trucksOnly: "trucks only",
  closed: "closed",
  other: "other",
};
const CONDITIONAL = /^(.*?)\s*@\s*\((.*)\)\s*$/s;

const dividerClass = (value) =>
  value === undefined || value === ""
    ? "fe-div-untagged"
    : DIVIDERS.includes(value)
      ? `fe-div-${value}`
      : "fe-div-other";

/** Colour key and label of a restriction class, as the map names and colours it. */
function renderClassKey(doc, className) {
  const node = el(doc, "span", "fe-class");
  if (!className) {
    node.append(el(doc, "span", "fe-muted", "open"));
    return node;
  }
  const key = el(doc, "span", "fe-ckey");
  key.style.background = ACCESS_CLASS_COLORS[className];
  node.append(key, CLASS_LABEL[className] ?? className);
  return node;
}

function stretchHeader(doc, s) {
  const head = el(doc, "div", "fe-lsh");
  const n = s.lanes ?? s.slotCount;
  const unit = s.lanes !== undefined ? "lane" : "slot";
  head.append(
    el(doc, "span", "fe-mono", `${s.startCm}–${s.endCm}`),
    ` · ${n} ${unit}${Number(n) === 1 ? "" : "s"}`,
  );
  const counts = Object.entries(s.counts).map(([name, v]) => `${name} ${v}`);
  if (counts.length) head.append(el(doc, "div", "fe-muted", counts.join(", ")));
  return head;
}

/**
 * @param {Document} doc
 * @param {{stretches: object[], usedTags: object[]}} view from buildLaneView
 */
export function renderLaneView(doc, view) {
  const node = el(doc, "div", "fe-lanes");
  node.append(el(doc, "div", "fe-vtitle", "lanes"));
  const usedKeys = new Set(
    view.usedTags.map((t) => t.key.replace(/:step$/, "")),
  );
  const drawn = ["lanes:direction", "divider:lanes"].filter((k) =>
    usedKeys.has(k),
  );
  const seenDir = new Set();
  const seenDiv = new Set();
  const windows = new Set();
  let hasAccess = false;

  view.stretches.forEach((s, index) => {
    const n = s.slotCount;
    const grid = el(doc, "div", "fe-ls");
    grid.dataset.index = String(index);
    grid.dataset.start = String(s.startCm);
    grid.dataset.end = String(s.endCm);
    grid.style.gridTemplateColumns = `136px repeat(${n}, ${n >= 6 ? 40 : 46}px)`;
    grid.append(stretchHeader(doc, s));
    for (let i = 0; i < n; i++) grid.append(el(doc, "div", "fe-lsn", i + 1));

    if (drawn.length) {
      const top = el(doc, "div", "fe-ltop");
      const label = el(doc, "div", "fe-lsl");
      drawn.forEach((k) => label.append(el(doc, "span", undefined, k)));
      top.append(label);
      for (let i = 0; i < n; i++) {
        const dir = s.direction ? s.direction[i] : undefined;
        const div = i > 0 ? (s.dividers ? s.dividers[i - 1] : undefined) : null;
        if (dir) seenDir.add(dir);
        if (div) seenDiv.add(div);
        const cell = el(
          doc,
          "div",
          "fe-lc",
          dir ? (DIR_GLYPH[dir] ?? "?") : "",
        );
        if (div !== null) cell.classList.add(dividerClass(div));
        const tip = [`slot ${i + 1}`];
        if (s.direction) tip.push(`lanes:direction ${dir || "(blank)"}`);
        if (div !== null && s.dividers)
          tip.push(`divider:lanes ${i}|${i + 1} ${div || "(blank)"}`);
        cell.dataset.tip = tip.join(" · ");
        top.append(cell);
      }
      grid.append(top);
    }

    s.rows.forEach((r) => {
      const row = el(doc, "div", "fe-lrow");
      const label = el(
        doc,
        "div",
        r.tagged ? "fe-lsl" : "fe-lsl fe-muted",
        r.key,
      );
      label.dataset.tip = r.tagged ? r.key : `${r.key} (not tagged)`;
      row.append(label);
      if (r.key.startsWith("lanes:vehicle_access:")) hasAccess = true;
      r.slots.forEach((value, i) => {
        const cell = el(doc, "div", "fe-lsv");
        cell.dataset.tip = `slot ${i + 1} · ${r.key} ${value || "(blank)"}`;
        if (value === "") {
          cell.append(el(doc, "span", "fe-blank", "·"));
        } else if (r.key === "turn:lanes") {
          const glyphs = splitUnescaped(value, ";")
            .map((t) => TURN_GLYPH[t] ?? "?")
            .join("");
          const backward = s.direction && s.direction[i] === "backward";
          cell.append(el(doc, "span", backward ? "fe-rot" : undefined, glyphs));
        } else {
          const match = CONDITIONAL.exec(value);
          if (match && match[1] !== "") {
            windows.add(match[2]);
            cell.textContent = `${match[1]} @`;
          } else {
            cell.textContent = value;
          }
        }
        row.append(cell);
      });
      grid.append(row);
    });

    if (s.classes) {
      const row = el(doc, "div", "fe-lrow");
      row.append(el(doc, "div", "fe-lsl", "class"));
      s.classes.forEach((c) => {
        const cell = el(doc, "div", "fe-lsv fe-cls");
        cell.append(renderClassKey(doc, c));
        row.append(cell);
      });
      grid.append(row);
    }
    node.append(grid);
  });

  const legend = [];
  if (seenDir.size)
    legend.push(
      [...seenDir].map((d) => `${DIR_GLYPH[d] ?? "?"} ${d}`).join(", "),
    );
  if (seenDiv.size)
    legend.push(
      "divider:lanes: " +
        [...seenDiv]
          .map((d) => DIVIDER_WORD[d] ?? `blue-grey = ${d}`)
          .join(", "),
    );
  windows.forEach((w) => legend.push(`@ (${w})`));
  if (hasAccess) legend.push("· blank");
  if (legend.length)
    node.append(el(doc, "div", "fe-legend", legend.join(" · ")));
  node.append(renderViewRaw(doc, view.usedTags));
  return node;
}

function cells(doc, tr, texts, tagName = "td") {
  texts.forEach((t) =>
    tr.append(typeof t === "string" ? el(doc, tagName, undefined, t) : t),
  );
}

/**
 * @param {Document} doc
 * @param {{stretches: object[], usedTags: object[]}} view from buildLmRestrictionView
 */
export function renderLmView(doc, view) {
  const node = el(doc, "div", "fe-lmview");
  node.append(el(doc, "div", "fe-vtitle", "lane restrictions"));
  view.stretches.forEach((s) => {
    if (view.stretches.length > 1)
      node.append(
        el(doc, "div", "fe-mono fe-muted", `${s.startCm}–${s.endCm}`),
      );
    const table = el(doc, "table", "fe-matrix");
    const head = el(doc, "tr");
    cells(doc, head, ["vehicle_restriction", "tagged", "result"], "th");
    table.append(head);
    s.general.forEach(({ token, value }) => {
      const tr = el(doc, "tr");
      cells(doc, tr, [token, value, ""]);
      table.append(tr);
    });
    s.modes.forEach((m) => {
      const tr = el(doc, "tr");
      const tagged = el(doc, "td");
      tagged.append(
        m.tagged === undefined ? el(doc, "span", "fe-blank", "·") : m.tagged,
      );
      cells(doc, tr, [m.mode, tagged, m.result]);
      table.append(tr);
    });
    s.hov.forEach(({ key, value }) => {
      const tr = el(doc, "tr");
      const td = el(doc, "td");
      td.colSpan = 2;
      td.append(renderPlainValue(doc, value));
      cells(doc, tr, [key, td]);
      table.append(tr);
    });
    const classRow = el(doc, "tr", "fe-clsrow");
    const td = el(doc, "td");
    td.colSpan = 2;
    td.append(renderClassKey(doc, s.className));
    cells(doc, classRow, ["class", td]);
    table.append(classRow);
    node.append(table);
  });
  node.append(renderViewRaw(doc, view.usedTags));
  return node;
}

/**
 * @param {Document} doc
 * @param {object} view from buildCajView
 * @param {[string, unknown][]} derivedRows from curvatureAtJunctionKeyValues
 */
export function renderCajView(doc, view, derivedRows) {
  const node = el(doc, "div", "fe-cajview");
  node.append(el(doc, "div", "fe-vtitle", "curvature at junction"));
  const table = el(doc, "table", "fe-matrix");
  const add = (label, value) => {
    const tr = el(doc, "tr");
    tr.append(el(doc, "td", "label", label));
    tr.append(
      typeof value === "object" ? value : el(doc, "td", undefined, value),
    );
    table.append(tr);
  };
  add("curvature_at_junction", view.value);
  derivedRows.forEach(([label, value]) => add(label, String(value)));
  node.append(table);
  const full = view.usedTags.find((t) => t.sections && t.lr === "linear");
  if (full && view.fullCurvature) {
    node.append(el(doc, "div", "fe-muted", "meta_full_curvature"));
    // Measured along the manoeuvre, not one way: the axis runs to its last point.
    node.append(renderProfile(doc, full, null));
    // Raw only for what the view redraws: the profile's chain.
    node.append(renderViewRaw(doc, [full]));
  }
  return node;
}
